import { useCallback, useEffect, useRef, useState } from 'react';

interface UseMapDragOptions {
    isMobile: boolean;
    isDesktop: boolean;
}

export const useMapDrag = ({ isMobile, isDesktop }: UseMapDragOptions) => {
    const mapPlanRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });

    const SLOWDOWN_FACTOR = isMobile ? 0.2 : 0.4;

    const handleMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
        setStartPosition({ x: e.pageX, y: e.pageY });
    }, []);

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (!isDragging || !mapPlanRef.current) return;

            const deltaX = (e.pageX - startPosition.x) * SLOWDOWN_FACTOR;
            const deltaY = (e.pageY - startPosition.y) * SLOWDOWN_FACTOR;

            mapPlanRef.current.scrollLeft -= deltaX;
            mapPlanRef.current.scrollTop -= deltaY;

            setStartPosition({ x: e.pageX, y: e.pageY });
        },
        [isDragging, startPosition, SLOWDOWN_FACTOR],
    );

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
    }, []);

    useEffect(() => {
        if (isDragging) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    const onMouseDown = useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (isDesktop) handleMouseDown(e);
        },
        [isDesktop, handleMouseDown],
    );

    return { mapPlanRef, isDragging, onMouseDown };
};
